import { BaseEnumResponse } from '@app/models/shared.models';

export const DefaultProjectStatusId = {
  Idea: 0,
  Draft: 1,
  AnalyseStartedByStaff: 2,
  AnalysedByStaff: 3,
  DataSupplyNeeded: 5,
  PublishReady: 6,
  Publishable: 7,
  Published: 8,
  ReadyForCollection: 10,
  ActiveCollectionAndRunning: 11,
  ClosedCollectionDone: 20,
  ClosedCollectionInsufficient: 21,
  RunningCollected: 30,
  Done: 31,
  RejectedPublish: -2,
  RejectedIdea: -1,
} as const;

export type ProjectStatusName = keyof typeof DefaultProjectStatusId;
export type ProjectStatusIds = Record<ProjectStatusName, number>;

export function resolveProjectStatusIds(
  statuses: BaseEnumResponse[] | null | undefined,
): ProjectStatusIds {
  const byName = new Map(
    (statuses ?? []).map((status) => [status.name?.trim(), status.id] as const),
  );
  const getId = (name: ProjectStatusName, aliases: string[] = []): number => {
    const direct = byName.get(name);
    if (direct != null) {
      return direct;
    }
    for (const alias of aliases) {
      const aliasId = byName.get(alias);
      if (aliasId != null) {
        return aliasId;
      }
    }
    return DefaultProjectStatusId[name];
  };

  return {
    Idea: getId('Idea'),
    Draft: getId('Draft'),
    AnalyseStartedByStaff: getId('AnalyseStartedByStaff'),
    AnalysedByStaff: getId('AnalysedByStaff'),
    DataSupplyNeeded: getId('DataSupplyNeeded'),
    PublishReady: getId('PublishReady'),
    Publishable: getId('Publishable'),
    Published: getId('Published'),
    ReadyForCollection: getId('ReadyForCollection'),
    ActiveCollectionAndRunning: getId('ActiveCollectionAndRunning'),
    ClosedCollectionDone: getId('ClosedCollectionDone'),
    ClosedCollectionInsufficient: getId('ClosedCollectionInsufficient', [
      'ClosedCollectionInsuficcient',
    ]),
    RunningCollected: getId('RunningCollected'),
    Done: getId('Done'),
    RejectedPublish: getId('RejectedPublish'),
    RejectedIdea: getId('RejectedIdea'),
  };
}

export type UserPrimaryActionKind = 'submitForReview' | 'publish';

export function getUserPrimaryAction(
  state: number | null | undefined,
  statusIds: ProjectStatusIds = DefaultProjectStatusId,
): UserPrimaryActionKind | null {
  if (state === undefined || state === null) {
    return null;
  }
  if (state === statusIds.Draft || state === statusIds.DataSupplyNeeded) {
    return 'submitForReview';
  }
  if (state === statusIds.PublishReady || state === statusIds.Publishable) {
    return 'publish';
  }
  return null;
}

export function getUserPrimaryButtonLabelKey(
  state: number | null | undefined,
  statusIds: ProjectStatusIds = DefaultProjectStatusId,
): 'submitForReview' | 'publish' {
  const immediate = getUserPrimaryAction(state, statusIds);
  if (immediate === 'submitForReview') {
    return 'submitForReview';
  }
  if (immediate === 'publish') {
    return 'publish';
  }
  if (
    state !== null &&
    state !== undefined &&
    state >= statusIds.Published
  ) {
    return 'publish';
  }
  if (
    state === statusIds.Idea ||
    state === statusIds.Draft ||
    state === statusIds.RejectedIdea
  ) {
    return 'submitForReview';
  }
  if (
    state !== null &&
    state !== undefined &&
    state >= statusIds.AnalyseStartedByStaff &&
    state < statusIds.Publishable
  ) {
    return 'publish';
  }
  return 'submitForReview';
}

export function getUserPrimaryHintKey(
  state: number | null | undefined,
  wizardStepsComplete: boolean,
  statusIds: ProjectStatusIds = DefaultProjectStatusId,
): string | null {
  const immediate = getUserPrimaryAction(state, statusIds);
  if (immediate === 'submitForReview' || immediate === 'publish') {
    return wizardStepsComplete
      ? null
      : 'projectWizard.primaryAction.hint.completeWizard';
  }
  if (state === statusIds.Idea) {
    return wizardStepsComplete
      ? 'projectWizard.primaryAction.hint.ideaNextStep'
      : 'projectWizard.primaryAction.hint.completeWizard';
  }
  if (state === statusIds.RejectedIdea) {
    return 'projectWizard.primaryAction.hint.rejected';
  }
  if (state === statusIds.RejectedPublish) {
    return 'projectWizard.primaryAction.hint.waitForPublishable';
  }
  if (
    state !== null &&
    state !== undefined &&
    state >= statusIds.AnalyseStartedByStaff &&
    state < statusIds.Publishable
  ) {
    return 'projectWizard.primaryAction.hint.waitForPublishable';
  }
  if (
    state !== null &&
    state !== undefined &&
    state > statusIds.Publishable
  ) {
    return 'projectWizard.primaryAction.hint.pipelineComplete';
  }
  return 'projectWizard.primaryAction.hint.notAvailable';
}

export type AdminNextPrimaryKind =
  | 'staffAnalyzeStarted'
  | 'staffAccepted'
  | 'dataSupplyNeeded'
  | 'publishReady'
  | 'publishable'
  | 'published';

export function getAdminNextPrimaryTransition(
  state: number | null | undefined,
  statusIds: ProjectStatusIds = DefaultProjectStatusId,
): AdminNextPrimaryKind | null {
  if (state === undefined || state === null) {
    return null;
  }
  if (state < statusIds.AnalyseStartedByStaff) {
    return 'staffAnalyzeStarted';
  }
  if (state === statusIds.AnalyseStartedByStaff) {
    return 'staffAccepted';
  }
  if (state === statusIds.AnalysedByStaff) {
    return 'dataSupplyNeeded';
  }
  if (state === statusIds.DataSupplyNeeded) {
    return 'publishReady';
  }
  if (state === statusIds.PublishReady) {
    return 'publishable';
  }
  return 'published';
}

export function showAdminApproveDecline(
  state: number | null | undefined,
  statusIds: ProjectStatusIds = DefaultProjectStatusId,
): boolean {
  return (
    state === statusIds.AnalyseStartedByStaff ||
    state === statusIds.DataSupplyNeeded
  );
}
